import { useState, useEffect, useRef, useCallback } from 'react'
import { Routes, Route, Link, useLocation, Navigate } from 'react-router-dom'
import { useAuth } from './hooks/useAuth'
import { api } from './api'
import { Login } from './pages/Login'
import { ErrorBoundary } from './components/ErrorBoundary'
import { LoadingSkeleton } from './components/LoadingSkeleton'
import { NotificationBell } from './components/NotificationBell'
import { CharacterSwitcher, type MeData } from './components/CharacterSwitcher'
import { Dashboard } from './pages/Dashboard'
import { Wallet } from './pages/Wallet'
import { Mining } from './pages/Mining'
import { Skills } from './pages/Skills'
import { Industry } from './pages/Industry'
import { Tax } from './pages/Tax'
import { Utility } from './pages/Utility'
import { Refinery } from './pages/Refinery'
import { Pipeline } from './pages/Pipeline'
import { SellOrders } from './pages/SellOrders'
import { HubCompare } from './pages/HubCompare'
import { Pricing } from './pages/Pricing'
import { SkillImpact } from './pages/SkillImpact'
import { InterhubArbitrage } from './pages/InterhubArbitrage'
import { Assets } from './pages/Assets'
import { PersonalStats } from './pages/PersonalStats'
import { Planetary } from './pages/Planetary'
import { BlueprintMinerals } from './pages/BlueprintMinerals'
import { MiningProfitability } from './pages/MiningProfitability'

type NavItem = {
  to: string
  label: string
}

type NavBucket = {
  label: string
  items: readonly NavItem[]
}

// Buckets agrupan las 16 pages siguiendo el workflow del industrialist:
// mine → refine → sell → manage ISK. La nav top muestra solo los 5 nombres
// de bucket + Dashboard; cada bucket abre un dropdown con sus sub-pages.
//
// User context (commit message): el owner es un pilot SOLO dentro de la corp
// (no la gestiona). Por eso los buckets reflejan el flujo diario de un
// industrialist individual, no "corp management" features.
const NAV_BUCKETS: readonly NavBucket[] = [
  {
    label: 'Mining',
    items: [
      { to: '/mining', label: 'History' },
      { to: '/mining-profitability', label: 'Profitability' },
      { to: '/refinery', label: 'Refinery' },
      { to: '/pipeline', label: 'Pipeline' },
      { to: '/personal', label: 'Stats' },
      { to: '/skill-impact', label: 'Skill Impact' },
      { to: '/planetary', label: 'Planetary' },
    ],
  },
  {
    label: 'Sell',
    items: [
      { to: '/pricing', label: 'Pricing' },
      { to: '/sell-orders', label: 'Sell Orders' },
      { to: '/hub-compare', label: 'Hub Compare' },
      { to: '/interhub', label: 'Inter-hub' },
      { to: '/utility', label: 'Utility' },
    ],
  },
  {
    label: 'Industry',
    items: [
      { to: '/industry', label: 'Industry' },
      { to: '/blueprint-minerals', label: 'BP → Ores' },
    ],
  },
  {
    label: 'Skills',
    items: [
      { to: '/skills', label: 'Skills' },
    ],
  },
  {
    label: 'Wallet',
    items: [
      { to: '/wallet', label: 'Wallet' },
      { to: '/assets', label: 'Assets' },
      { to: '/tax', label: 'Tax' },
    ],
  },
]

export default function App() {
  const { data: me, isLoading } = useAuth()
  const location = useLocation()
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  // ⚠ Este useCallback DEBE estar antes de los early returns (auth loading,
  // redirects por estado de auth) para no violar las rules of hooks.
  // Cuando auth transiciona loading/unauth → authed, el hook count pasaba de
  // 3 a 4 → React #310 → toda la app crasheaba.
  const closeMobileNav = useCallback(() => setMobileNavOpen(false), [])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSkeleton variant="card" />
      </div>
    )
  }

  // Si no está autenticado y NO está en /login → redirigir
  if (!me?.authenticated && !location.pathname.startsWith('/login')) {
    return <Navigate to="/login" replace />
  }

  if (me?.authenticated && location.pathname === '/login') {
    return <Navigate to="/" replace />
  }

  if (!me?.authenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    )
  }

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-eve-border bg-gradient-to-b from-eve-bg to-eve-panel/95 backdrop-blur sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <svg width="24" height="24" viewBox="0 0 32 32" className="text-eve-accent transition-transform group-hover:rotate-12">
              <path d="M16 4 L28 12 L24 28 L8 28 L4 12 Z" fill="none" stroke="currentColor" strokeWidth="2"/>
              <circle cx="16" cy="16" r="3" fill="currentColor"/>
            </svg>
            <span className="font-display text-xl font-semibold tracking-wide text-eve-text">
              EVE <span className="text-eve-accent">Dashboard</span>
            </span>
          </Link>

          {/* Desktop nav (md+) */}
          <nav className="hidden md:flex items-center gap-1">
            <NavLink to="/" label="Dashboard" />
            {NAV_BUCKETS.map((bucket) => (
              <NavDropdown
                key={bucket.label}
                label={bucket.label}
                items={bucket.items}
                currentPath={location.pathname}
              />
            ))}
          </nav>

          {/* Mobile hamburger (<md) */}
          <button
            type="button"
            onClick={() => setMobileNavOpen(true)}
            className="md:hidden p-2 rounded text-eve-muted hover:text-eve-text hover:bg-eve-panel/60 transition-colors"
            aria-label="Open menu"
            aria-expanded={mobileNavOpen}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
              <path d="M3 5 H17 M3 10 H17 M3 15 H17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
          </button>

          {/* Desktop (>=sm): bell + character switcher (portrait + name +
              multi-char + link + logout). El switcher es el source of truth
              de "quién soy" en el header. */}
          <div className="hidden sm:flex items-center gap-3">
            <NotificationBell />
            <CharacterSwitcher me={me} />
          </div>
        </div>
      </header>

      {/* Mobile drawer (<md) */}
      <MobileNav
        open={mobileNavOpen}
        onClose={closeMobileNav}
        buckets={NAV_BUCKETS}
        currentPath={location.pathname}
        me={me}
      />

      <main className="flex-1 max-w-6xl mx-auto w-full px-4 py-8">
        <Routes>
          <Route path="/" element={<ErrorBoundary label="Dashboard"><Dashboard /></ErrorBoundary>} />
          <Route path="/wallet" element={<ErrorBoundary label="Wallet"><Wallet /></ErrorBoundary>} />
          <Route path="/mining" element={<ErrorBoundary label="Mining"><Mining /></ErrorBoundary>} />
          <Route path="/industry" element={<ErrorBoundary label="Industry"><Industry /></ErrorBoundary>} />
          <Route path="/skills" element={<ErrorBoundary label="Skills"><Skills /></ErrorBoundary>} />
          <Route path="/tax" element={<ErrorBoundary label="Tax"><Tax /></ErrorBoundary>} />
          <Route path="/utility" element={<ErrorBoundary label="Utility"><Utility /></ErrorBoundary>} />
          <Route path="/refinery" element={<ErrorBoundary label="Refinery"><Refinery /></ErrorBoundary>} />
          <Route path="/pipeline" element={<ErrorBoundary label="Pipeline"><Pipeline /></ErrorBoundary>} />
          <Route path="/sell-orders" element={<ErrorBoundary label="Sell Orders"><SellOrders /></ErrorBoundary>} />
          <Route path="/hub-compare" element={<ErrorBoundary label="Hub Compare"><HubCompare /></ErrorBoundary>} />
          <Route path="/pricing" element={<ErrorBoundary label="Pricing"><Pricing /></ErrorBoundary>} />
          <Route path="/skill-impact" element={<ErrorBoundary label="Skill Impact"><SkillImpact /></ErrorBoundary>} />
          <Route path="/interhub" element={<ErrorBoundary label="Inter-hub"><InterhubArbitrage /></ErrorBoundary>} />
          <Route path="/personal" element={<ErrorBoundary label="Personal Stats"><PersonalStats /></ErrorBoundary>} />
          <Route path="/assets" element={<ErrorBoundary label="Assets"><Assets /></ErrorBoundary>} />
          <Route path="/planetary" element={<ErrorBoundary label="Planetary"><Planetary /></ErrorBoundary>} />
          <Route path="/blueprint-minerals" element={<ErrorBoundary label="BP → Ores"><BlueprintMinerals /></ErrorBoundary>} />
          <Route path="/mining-profitability" element={<ErrorBoundary label="Mining Profitability"><MiningProfitability /></ErrorBoundary>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  )
}

function NavLink({ to, label }: { to: string; label: string }) {
  const location = useLocation()
  const active = location.pathname === to
  return (
    <Link to={to} className={`nav-link ${active ? 'active' : ''}`}>
      {label}
    </Link>
  )
}

function NavDropdown({
  label,
  items,
  currentPath,
}: {
  label: string
  items: readonly NavItem[]
  currentPath: string
}) {
  const [open, setOpen] = useState(false)
  const [focusedIdx, setFocusedIdx] = useState(-1)
  const ref = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const itemRefs = useRef<(HTMLAnchorElement | null)[]>([])
  const isActive = items.some((i) => i.to === currentPath)

  // Reset focused index on open; when closed, return focus to trigger
  useEffect(() => {
    if (open) {
      setFocusedIdx(0)
    } else {
      setFocusedIdx(-1)
    }
  }, [open])

  // Apply focus when focusedIdx changes
  useEffect(() => {
    if (open && focusedIdx >= 0) {
      itemRefs.current[focusedIdx]?.focus()
    }
  }, [open, focusedIdx])

  // Click outside to close
  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  // Escape to close + return focus to trigger
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
        buttonRef.current?.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  const onButtonKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      setOpen(true)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setOpen(true)
    }
  }

  const onItemKeyDown = (e: React.KeyboardEvent, i: number) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setFocusedIdx((i + 1) % items.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setFocusedIdx((i - 1 + items.length) % items.length)
    } else if (e.key === 'Home') {
      e.preventDefault()
      setFocusedIdx(0)
    } else if (e.key === 'End') {
      e.preventDefault()
      setFocusedIdx(items.length - 1)
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setOpen(false)
      buttonRef.current?.focus()
    } else if (e.key === 'Tab') {
      // Allow natural Tab but close on tab-out
      setOpen(false)
    }
  }

  return (
    <div ref={ref} className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onButtonKeyDown}
        aria-expanded={open}
        aria-haspopup="menu"
        className={`nav-link flex items-center gap-1 ${isActive ? 'active' : ''}`}
      >
        {label}
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          className={`transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        >
          <path
            d="M2 4 L5 7 L8 4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute top-full left-0 mt-1 min-w-[200px] bg-eve-panel border border-eve-border rounded-md shadow-lg py-1 z-20"
        >
          {items.map((item, i) => {
            const isCurrent = item.to === currentPath
            return (
              <Link
                key={item.to}
                ref={(el) => {
                  itemRefs.current[i] = el
                }}
                to={item.to}
                role="menuitem"
                tabIndex={focusedIdx === i ? 0 : -1}
                onClick={() => setOpen(false)}
                onKeyDown={(e) => onItemKeyDown(e, i)}
                className={`block px-3 py-2 text-sm hover:bg-eve-bg/60 transition-colors ${
                  isCurrent ? 'text-eve-accent' : 'text-eve-text'
                }`}
              >
                {item.label}
              </Link>
            )
          })}
        </div>
      )}
    </div>
  )
}

function MobileNav({
  open,
  onClose,
  buckets,
  currentPath,
  me,
}: {
  open: boolean
  onClose: () => void
  buckets: readonly NavBucket[]
  currentPath: string
  /** Para alimentar el switcher dentro del drawer (<sm). */
  me: MeData
}) {
  const drawerRef = useRef<HTMLElement>(null)

  // Close on route change (after a nav from the drawer)
  useEffect(() => {
    if (open) onClose()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath])

  // Lock body scroll when open
  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  // Focus trap + Escape when open
  useEffect(() => {
    if (!open) return
    drawerRef.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        aria-hidden="true"
        className={`fixed inset-0 bg-black/60 z-30 md:hidden transition-opacity ${
          open ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      />
      {/* Drawer */}
      <aside
        ref={drawerRef}
        tabIndex={-1}
        aria-label="Main menu"
        aria-hidden={!open}
        className={`fixed top-0 left-0 h-full w-72 bg-eve-panel border-r border-eve-border z-40 md:hidden transform transition-transform ${
          open ? 'translate-x-0' : '-translate-x-full'
        } overflow-y-auto`}
      >
        <div className="p-4 border-b border-eve-border flex items-center justify-between sticky top-0 bg-eve-panel z-10">
          <span className="font-display text-lg text-eve-text">Menu</span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            className="p-1 rounded text-eve-muted hover:text-eve-text hover:bg-eve-bg/60 transition-colors"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
              <path d="M4 4 L14 14 M14 4 L4 14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
        <nav className="p-4 space-y-5">
          <Link
            to="/"
            onClick={onClose}
            className={`block py-2 text-sm font-medium rounded px-2 ${
              currentPath === '/'
                ? 'bg-eve-accent/10 text-eve-accent'
                : 'text-eve-text hover:bg-eve-bg/60'
            }`}
          >
            Dashboard
          </Link>
          {buckets.map((bucket) => {
            const bucketActive = bucket.items.some((i) => i.to === currentPath)
            return (
              <div key={bucket.label}>
                <div
                  className={`text-[10px] uppercase tracking-[0.1em] font-semibold mb-2 px-2 ${
                    bucketActive ? 'text-eve-accent' : 'text-eve-muted'
                  }`}
                >
                  {bucket.label}
                </div>
                <ul className="space-y-0.5">
                  {bucket.items.map((item) => {
                    const isCurrent = item.to === currentPath
                    return (
                      <li key={item.to}>
                        <Link
                          to={item.to}
                          onClick={onClose}
                          className={`block px-2 py-1.5 rounded text-sm transition-colors ${
                            isCurrent
                              ? 'bg-eve-accent/10 text-eve-accent'
                              : 'text-eve-text hover:bg-eve-bg/60'
                          }`}
                        >
                          {item.label}
                        </Link>
                      </li>
                    )
                  })}
                </ul>
              </div>
            )
          })}
          {/* Mobile (<sm) account controls: mismo switcher que el header.
              En desktop el switcher vive en el header bar; acá vive dentro
              del drawer. */}
          <div className="sm:hidden border-t border-eve-border pt-4">
            <div className="text-[10px] uppercase tracking-[0.1em] font-semibold mb-2 px-2 text-eve-muted">
              Cuenta
            </div>
            <div className="px-2">
              <CharacterSwitcher me={me} />
            </div>
          </div>
        </nav>
      </aside>
    </>
  )
}
